import { IQueryParams } from './queryparams';

/** Mirrors the API's vehicle and general risk inspections (`/inspections`). */
export type InspectionStatus =
  | 'Draft'
  | 'Submitted'
  | 'UnderReview'
  | 'Approved'
  | 'Rejected'
  | 'RetakeRequested'
  | 'Expired'
  | 'Cancelled';

export type InspectionChannel = 'SelfService' | 'Intermediary' | 'Insurer' | 'Admin' | 'Partner';

export type InspectionMediaKind = 'Photo' | 'Video';

/** What is inspected: a vehicle on a motor policy, or a general risk (a building, equipment, stock...). */
export type InspectionKind = 'Motor' | 'General';

/** A slot key: a product's own photo key, e.g. 'Front' or 'FrontOfTheHouse', or the walk-around video. */
export type InspectionSlot = string;

/** The only video slot. */
export const WALK_AROUND: InspectionSlot = 'WalkAround';

/** One photo a product asks for. An empty key on a new one is derived from the label by the API. */
export interface IInspectionSlotDefinition {
  key: InspectionSlot;
  label: string;
  hint?: string | null;
}

export type InspectionEventType =
  | 'Created'
  | 'MediaAdded'
  | 'MediaReplaced'
  | 'MediaRemoved'
  | 'Submitted'
  | 'ReviewStarted'
  | 'Approved'
  | 'Rejected'
  | 'RetakeRequested'
  | 'Expired'
  | 'Cancelled'
  | 'ClientNotified';

/** What a motor product asks for when it doesn't list its own (the API's defaults, in order). */
export const MOTOR_INSPECTION_SLOTS: readonly IInspectionSlotDefinition[] = [
  { key: 'Front', label: 'Front', hint: 'The whole front with the number plate readable' },
  { key: 'Back', label: 'Back', hint: 'The whole back with the number plate readable' },
  { key: 'Left', label: 'Left side', hint: 'The whole left side, front to back' },
  { key: 'Right', label: 'Right side', hint: 'The whole right side, front to back' },
  { key: 'Chassis', label: 'Chassis number', hint: 'Close up, every character readable' },
  { key: 'Engine', label: 'Engine', hint: 'Bonnet open, engine number visible if you can' },
];

/** A slot's label from the inspection's own slots, falling back to the key. */
export function inspectionSlotLabel(inspection: { slots: IInspectionSlotDefinition[] }, slot: InspectionSlot): string {
  if (slot === WALK_AROUND) return 'Walk-around video';
  return inspection.slots.find((s) => s.key === slot)?.label ?? slot;
}

/** Client-side copies of the API's limits, so a too-big file fails before it is uploaded. */
export const INSPECTION_LIMITS = {
  photoBytes: 10 * 1024 * 1024,
  videoBytes: 100 * 1024 * 1024,
  /** The apps stop recording at a minute; the API allows two. */
  videoSeconds: 60,
} as const;

/** Open inspections can still change; the rest are final. */
export const INSPECTION_OPEN: readonly InspectionStatus[] = [
  'Draft',
  'RetakeRequested',
  'Submitted',
  'UnderReview',
];

export interface IInspectionActor {
  id: number;
  name: string;
  role: string;
  /** Staff-facing only; null for clients. */
  email?: string | null;
}

export interface IInspectionMediaRequest {
  kind: InspectionMediaKind;
  slot: InspectionSlot;
  url: string;
  capturedAt: string;
  latitude?: number | null;
  longitude?: number | null;
  accuracyMeters?: number | null;
  sha256: string;
  sizeBytes: number;
  durationSeconds?: number | null;
  deviceInfo?: string | null;
}

export interface IInspectionMedia extends IInspectionMediaRequest {
  id: number;
  createdAt: string;
  updatedAt: string;
  retakeRequested: boolean;
  reviewerComment: string | null;
}

export interface IInspectionEvent {
  id: number;
  createdAt: string;
  event: InspectionEventType;
  fromStatus: InspectionStatus | null;
  toStatus: InspectionStatus | null;
  notes: string | null;
  metadata: Record<string, unknown> | null;
  /** Null for the system (expiry, policy issue). */
  createdBy: IInspectionActor | null;
}

export interface IInspection {
  id: number;
  createdAt: string;
  updatedAt: string;
  kind: InspectionKind;
  /** Motor only. */
  vehicleId: number | null;
  clientId: number;
  insurerId: number;
  intermediaryId: number | null;
  /** The agency or broker the inspection came through. */
  intermediaryName?: string | null;
  partnerId?: number | null;
  partnerName?: string | null;
  productId: number | null;
  policyId: number | null;
  motorRiskId: number | null;
  generalRiskId: number | null;
  /** What is inspected: the registration, or the general risk's description. */
  title: string | null;
  /** "Toyota Corolla 2020", or the risk type and location, e.g. "Building · Plot 12, Kabulonga". */
  subtitle: string | null;
  status: InspectionStatus;
  channel: InspectionChannel;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
  validUntil: string | null;
  initiatedBy: IInspectionActor | null;
  submittedBy: IInspectionActor | null;
  reviewedBy: IInspectionActor | null;
  /** The caller may approve, reject or ask for a retake once it's submitted; false in lists. */
  canReview?: boolean;
  missingSlots: InspectionSlot[];
  /** What the product asks for (the walk-around too when a video is required); still sent after submit. */
  requiredSlots: InspectionSlot[];
  /** What the product asks for, in order, with labels and hints (the walk-around last when a video is required). */
  slots: IInspectionSlotDefinition[];
  media: IInspectionMedia[];
  /** Newest first; empty in lists. */
  events: IInspectionEvent[];
  /** Motor only; prefer title. */
  vehicleRegistration: string | null;
  /** Motor only; prefer subtitle. */
  vehicleDescription: string | null;
  clientName: string | null;
  policyNumber: string | null;
  insurerName: string | null;
  productName: string | null;
}

export interface IInspectionQueryParams extends IQueryParams {
  /** Sent as repeated `status` params. */
  status?: InspectionStatus[] | null;
  kind?: InspectionKind | null;
  /** Registration, risk description, policy number or client name. */
  search?: string | null;
  vehicleId?: number | null;
  policyId?: number | null;
  clientId?: number | null;
  from?: string | null;
  to?: string | null;
}

/** A motor inspection names the vehicle; a general one the general risk or policy. */
export interface IInspectionCreateRequest {
  vehicleId?: number | null;
  policyId?: number | null;
  motorRiskId?: number | null;
  generalRiskId?: number | null;
  productId?: number | null;
}

/** A motor or general product's inspection rules; null on a product means inspections are off. */
export interface IInspectionSettings {
  required: boolean;
  validityDays: number;
  allowIssueBeforeApproval: boolean;
  requireVideo: boolean;
  /** The photos to take, in order (max 20). Null on a motor product means MOTOR_INSPECTION_SLOTS. */
  slots: IInspectionSlotDefinition[] | null;
}

export const DEFAULT_INSPECTION_SETTINGS: IInspectionSettings = {
  required: false,
  validityDays: 365,
  allowIssueBeforeApproval: true,
  requireVideo: false,
  slots: null,
};

/** Who the inspection came through: "Agent · Hobbiton Agency", or the channel ("Self service", "Insurer"). */
export function inspectionSourceLabel(i: IInspection): string {
  if (i.intermediaryName) {
    const role = [i.submittedBy?.role, i.initiatedBy?.role].find((r) => r === 'Agent' || r === 'Broker');
    return `${role ?? 'Intermediary'} · ${i.intermediaryName}`;
  }
  if (i.partnerName) return `Partner · ${i.partnerName}`;
  return i.channel === 'SelfService' ? 'Self service' : i.channel;
}

export function inspectionStatusColor(status: InspectionStatus): string {
  switch (status) {
    case 'Approved':
      return 'green';
    case 'Rejected':
      return 'red';
    case 'Submitted':
      return 'blue';
    case 'UnderReview':
      return 'geekblue';
    case 'RetakeRequested':
      return 'orange';
    case 'Draft':
      return 'gold';
    default:
      return 'default';
  }
}

/** Whether a motor quotation can be issued as far as inspections go (GET /inspections/issue-check). */
export interface IInspectionIssueCheck {
  /** The insurer's product or the broker requires an approved inspection of each vehicle before issue. */
  required: boolean;
  vehicles: IVehicleInspectionCheck[];
}

export interface IVehicleInspectionCheck {
  motorRiskId: number;
  vehicleId: number;
  /** Has what issue needs (always, when nothing is required). */
  approved: boolean;
  /** The approved inspection that counts, else the open one on this quotation. */
  inspectionId: number | null;
  status: InspectionStatus | null;
}

export const INSPECTION_STATUS_LABELS: Record<InspectionStatus, string> = {
  Draft: 'Draft',
  Submitted: 'Submitted',
  UnderReview: 'Under review',
  Approved: 'Approved',
  Rejected: 'Rejected',
  RetakeRequested: 'Retake requested',
  Expired: 'Expired',
  Cancelled: 'Cancelled',
};
